import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'

const AdminNotifications = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, today: 0, by_type: {} })
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [filterType, setFilterType] = useState('')
  const [editRequestStats, setEditRequestStats] = useState({ pending: 0, total: 0 })
  const [showSendForm, setShowSendForm] = useState(false)
  const [sendTo, setSendTo] = useState('all')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [usersList, setUsersList] = useState([])
  const [sendForm, setSendForm] = useState({ title: '', message: '', type: 'general' })
  const [sending, setSending] = useState(false)

  useEffect(() => {
    loadNotifications()
    loadStats()
    loadEditRequestStats()
  }, [filterType])

  const loadUsers = async () => {
    try {
      const response = await api.get('/client-admin/users/dropdown')
      if (response.data.success) {
        setUsersList(response.data.data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const handleOpenSendForm = () => {
    setShowSendForm(true)
    setSendForm({ title: '', message: '', type: 'general' })
    setSendTo('all')
    setSelectedUsers([])
    loadUsers()
  }

  const handleSendNotification = async (e) => {
    e.preventDefault()
    if (!sendForm.title.trim() || !sendForm.message.trim()) {
      alert('Please fill title and message')
      return
    }
    if (sendTo === 'specific' && selectedUsers.length === 0) {
      alert('Please select at least one user')
      return
    }

    setSending(true)
    try {
      const payload = {
        title: sendForm.title,
        message: sendForm.message,
        type: sendForm.type,
        send_to: sendTo,
      }
      if (sendTo === 'specific') {
        payload.user_ids = selectedUsers
      }

      const response = await api.post('/client-admin/notifications/send', payload)
      if (response.data.success) {
        alert(response.data.message || 'Notification sent successfully!')
        setShowSendForm(false)
        loadNotifications()
        loadStats()
      }
    } catch (error) {
      console.error('Error sending notification:', error)
      alert(error.response?.data?.message || 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    )
  }

  const loadEditRequestStats = async () => {
    try {
      const response = await api.get('/client-admin/member-edit-requests/stats')
      if (response.data.success) {
        setEditRequestStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error loading edit request stats:', error)
    }
  }

  const loadNotifications = async (page = 1) => {
    try {
      setLoading(true)
      const params = new URLSearchParams({ page })
      if (filterType) params.append('type', filterType)

      const response = await api.get(`/client-admin/notifications?${params}`)
      const data = response.data

      if (data.notifications) {
        setNotifications(data.notifications.data || [])
        setPagination({
          current_page: data.notifications.current_page,
          last_page: data.notifications.last_page,
          total: data.notifications.total
        })
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await api.get('/client-admin/notifications/stats')
      setStats(response.data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this notification?')) return

    try {
      await api.delete(`/client-admin/notifications/${id}`)
      loadNotifications(pagination.current_page)
      loadStats()
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'tournament_edit': return 'fas fa-edit'
      case 'tournament_publish': return 'fas fa-eye'
      case 'tournament_unpublish': return 'fas fa-eye-slash'
      case 'error': return 'fas fa-exclamation-circle'
      case 'warning': return 'fas fa-exclamation-triangle'
      case 'success': return 'fas fa-check-circle'
      case 'info': return 'fas fa-info-circle'
      case 'player_registration_request': return 'fas fa-user-plus'
      default: return 'fas fa-bell'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'tournament_edit': return '#3b82f6'
      case 'tournament_publish': return '#10b981'
      case 'tournament_unpublish': return '#f59e0b'
      case 'error': return '#dc2626'
      case 'warning': return '#f59e0b'
      case 'success': return '#10b981'
      case 'info': return '#3b82f6'
      case 'player_registration_request': return '#8b5cf6'
      default: return '#6b7280'
    }
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'tournament_edit': return 'Tournament Edited'
      case 'tournament_publish': return 'Tournament Published'
      case 'tournament_unpublish': return 'Tournament Unpublished'
      case 'error': return 'Important / Urgent'
      case 'warning': return 'Warning'
      case 'success': return 'Success'
      case 'info': return 'Information'
      case 'general': return 'General'
      case 'player_registration_request': return 'Registration Request'
      default: return type.replace(/_/g, ' ')
    }
  }

  return (
    <div className="admin-notifications">
      {/* Header */}
      <div className="page-header" style={{
        background: 'linear-gradient(135deg, #022a5e 0%, #053e88 100%)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            <i className="fas fa-bell" style={{ marginRight: '10px' }}></i>
            Notifications
          </h1>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
            View all notifications sent to users
          </p>
          <button
            onClick={handleOpenSendForm}
            style={{
              marginTop: '1rem',
              padding: '0.6rem 1.2rem',
              background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <i className="fas fa-paper-plane"></i> Send Notification
          </button>
        </div>
        <button
          onClick={() => navigate('/admin/dashboard')}
          style={{
            background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '10px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.85rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Send Notification Modal */}
      {showSendForm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '550px',
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              padding: '16px 20px', borderBottom: '2px solid #f3f4f6',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              position: 'sticky', top: 0, background: '#fff', zIndex: 1
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                <i className="fas fa-paper-plane" style={{ marginRight: '8px', color: '#022a5e' }}></i>
                Send Notification
              </h3>
              <button onClick={() => setShowSendForm(false)} style={{
                width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                background: '#f3f4f6', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#666'
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSendNotification} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={sendForm.title}
                  onChange={(e) => setSendForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Notification title"
                  required
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  Message *
                </label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Write your notification message..."
                  required
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                  Type
                </label>
                <select
                  value={sendForm.type}
                  onChange={(e) => setSendForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'inherit'
                  }}
                >
                  <option value="general">General</option>
                  <option value="info">Information</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="error">Important / Urgent</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Send To *
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{
                    flex: 1, padding: '12px', border: sendTo === 'all' ? '2px solid #022a5e' : '2px solid #e5e7eb',
                    borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                    background: sendTo === 'all' ? '#eff6ff' : '#fff', transition: 'all 0.2s'
                  }}>
                    <input type="radio" value="all" checked={sendTo === 'all'} onChange={() => setSendTo('all')} style={{ display: 'none' }} />
                    <i className="fas fa-users" style={{ display: 'block', fontSize: '1.2rem', marginBottom: '4px', color: sendTo === 'all' ? '#022a5e' : '#9ca3af' }}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: sendTo === 'all' ? '#022a5e' : '#6b7280' }}>All Users</span>
                  </label>
                  <label style={{
                    flex: 1, padding: '12px', border: sendTo === 'specific' ? '2px solid #022a5e' : '2px solid #e5e7eb',
                    borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
                    background: sendTo === 'specific' ? '#eff6ff' : '#fff', transition: 'all 0.2s'
                  }}>
                    <input type="radio" value="specific" checked={sendTo === 'specific'} onChange={() => setSendTo('specific')} style={{ display: 'none' }} />
                    <i className="fas fa-user-check" style={{ display: 'block', fontSize: '1.2rem', marginBottom: '4px', color: sendTo === 'specific' ? '#022a5e' : '#9ca3af' }}></i>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: sendTo === 'specific' ? '#022a5e' : '#6b7280' }}>Specific Users</span>
                  </label>
                </div>
              </div>

              {sendTo === 'specific' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Select Users ({selectedUsers.length} selected)
                  </label>
                  <div style={{
                    maxHeight: '200px', overflow: 'auto', border: '1px solid #e5e7eb',
                    borderRadius: '8px', padding: '4px'
                  }}>
                    {usersList.length === 0 ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                        <i className="fas fa-spinner fa-spin"></i> Loading users...
                      </div>
                    ) : (
                      usersList.map(user => (
                        <label
                          key={user.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
                            background: selectedUsers.includes(user.id) ? '#eff6ff' : 'transparent',
                            transition: 'background 0.15s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1f2937' }}>
                              {user.name}
                            </div>
                            {user.status && (
                              <div style={{ fontSize: '0.7rem', color: user.status === 'active' ? '#10b981' : '#9ca3af' }}>{user.status}</div>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                <button type="button" onClick={() => setShowSendForm(false)} disabled={sending}
                  style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff', color: '#555', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>
                  Cancel
                </button>
                <button type="submit" disabled={sending}
                  style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: '#022a5e', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', opacity: sending ? 0.6 : 1 }}>
                  {sending ? <><i className="fas fa-spinner fa-spin"></i> Sending...</> : <><i className="fas fa-paper-plane"></i> Send</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Requests Button */}
      <div
        onClick={() => navigate('/admin/member-edit-requests')}
        style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem'
          }}>
            <i className="fas fa-user-edit"></i>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
              Member Edit Requests
            </h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
              Review and approve member profile change requests
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {editRequestStats.pending > 0 && (
            <div style={{
              background: '#fff',
              color: '#d97706',
              padding: '0.35rem 0.85rem',
              borderRadius: '20px',
              fontWeight: '700',
              fontSize: '0.9rem'
            }}>
              {editRequestStats.pending} Pending
            </div>
          )}
          <i className="fas fa-chevron-right" style={{ fontSize: '1.1rem', opacity: 0.8 }}></i>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Notifications</div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1f2937' }}>{stats.total}</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Sent Today</div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#10b981' }}>{stats.today}</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Edit Notifications</div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#3b82f6' }}>{stats.by_type?.tournament_edit || 0}</div>
        </div>
        <div style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Publish/Unpublish</div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#f59e0b' }}>
            {(stats.by_type?.tournament_publish || 0) + (stats.by_type?.tournament_unpublish || 0)}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div style={{
        background: 'white',
        padding: '1rem 1.5rem',
        borderRadius: '10px',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <span style={{ fontWeight: '500', color: '#4b5563' }}>Filter by Type:</span>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            fontSize: '0.9rem'
          }}
        >
          <option value="">All Types</option>
          <option value="tournament_edit">Tournament Edited</option>
          <option value="tournament_publish">Tournament Published</option>
          <option value="tournament_unpublish">Tournament Unpublished</option>
        </select>
      </div>

      {/* Notifications List */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
            Notification History
          </h3>
          <span style={{
            background: '#f3f4f6',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.8rem',
            color: '#6b7280'
          }}>
            {pagination.total} total
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-bell-slash" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
            <p>No notifications found</p>
          </div>
        ) : (
          <div>
            {notifications.map(notification => (
              <div
                key={notification.id}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: `${getTypeColor(notification.type)}15`,
                  color: getTypeColor(notification.type),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className={getTypeIcon(notification.type)}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                        {notification.title}
                      </h4>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        background: `${getTypeColor(notification.type)}15`,
                        color: getTypeColor(notification.type),
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        marginTop: '4px'
                      }}>
                        {getTypeLabel(notification.type)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(notification.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px'
                      }}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5 }}>
                    {notification.message}
                  </p>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <span>
                      <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
                      {formatDate(notification.created_at)}
                    </span>
                    {notification.event && (
                      <span>
                        <i className="fas fa-calendar" style={{ marginRight: '4px' }}></i>
                        {notification.event.event_name}
                      </span>
                    )}
                    {notification.recipient_count > 0 && (
                      <span style={{
                        background: '#eff6ff',
                        color: '#2563eb',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: '600',
                        fontSize: '0.7rem'
                      }}>
                        <i className="fas fa-users" style={{ marginRight: '4px' }}></i>
                        Sent to {notification.recipient_count} {notification.recipient_count === 1 ? 'user' : 'users'}
                      </span>
                    )}
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div style={{
            padding: '1rem',
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => loadNotifications(page)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: page === pagination.current_page ? '2px solid #c00f15' : '1px solid #e5e7eb',
                  background: page === pagination.current_page ? '#fef2f2' : 'white',
                  color: page === pagination.current_page ? '#c00f15' : '#4b5563',
                  fontWeight: page === pagination.current_page ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}

export default AdminNotifications
